import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Users, Search, Mail, CalendarDays, ChevronRight, ArrowLeft, DollarSign, MapPin, Shield, ShieldOff, Loader2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminCustomers = () => {
  const { token, user: currentAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [viewMode, setViewMode] = useState('customers'); // 'customers' or 'users'
  const [roleDialog, setRoleDialog] = useState({ open: false, user: null, newRole: '' });
  const [roleChanging, setRoleChanging] = useState(false);
  const [roleHistory, setRoleHistory] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, customer: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchAllUsers();
    fetchRoleHistory();
  }, []);

  const fetchCustomers = async (search = '') => {
    try {
      const url = search
        ? `${API_URL}/api/admin/customers?search=${encodeURIComponent(search)}`
        : `${API_URL}/api/admin/customers`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetail = async (customerId) => {
    setDetailLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/customers/${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCustomerDetail(data);
    } catch (error) {
      console.error('Error fetching customer detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchRoleHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/roles/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setRoleHistory(await response.json());
      }
    } catch (error) {
      console.error('Error fetching role history:', error);
    }
  };

  const changeUserRole = async () => {
    if (!roleDialog.user || !roleDialog.newRole) return;
    setRoleChanging(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${roleDialog.user.id}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: roleDialog.newRole })
      });
      if (response.ok) {
        fetchAllUsers();
        fetchCustomers();
        fetchRoleHistory();
        setRoleDialog({ open: false, user: null, newRole: '' });
      } else {
        const err = await response.json();
        alert(err.detail || 'Fehler beim Ändern der Rolle');
      }
    } catch (error) {
      console.error('Error changing role:', error);
    } finally {
      setRoleChanging(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (value.length >= 2 || value.length === 0) {
      fetchCustomers(value);
    }
  };

  const deleteCustomer = async () => {
    if (!deleteDialog.customer) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/customers/${deleteDialog.customer.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchCustomers();
        fetchAllUsers();
        setDeleteDialog({ open: false, customer: null });
        if (selectedCustomer?.id === deleteDialog.customer.id) {
          setSelectedCustomer(null);
          setCustomerDetail(null);
        }
      } else {
        const err = await response.json();
        alert(err.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerDetail(customer.id);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Ausstehend', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      paid: { label: 'Bezahlt', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      confirmed: { label: 'Bestätigt', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      rejected: { label: 'Abgelehnt', className: 'bg-red-50 text-red-700 border-red-200' },
      cancelled: { label: 'Storniert', className: 'bg-slate-50 text-slate-700 border-slate-200' },
      expired: { label: 'Abgelaufen', className: 'bg-slate-50 text-slate-500 border-slate-200' },
    };
    const c = config[status] || config.pending;
    return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  // Customer Detail View
  if (selectedCustomer && customerDetail) {
    return (
      <div data-testid="admin-customer-detail">
        <Button variant="ghost" onClick={() => { setSelectedCustomer(null); setCustomerDetail(null); }} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Zurück zur Kundenliste
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer Info */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="text-lg">{customerDetail.name}</CardTitle>
              <CardDescription>{customerDetail.email}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                <span>{customerDetail.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CalendarDays className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                <span>Registriert: {format(new Date(customerDetail.created_at), 'dd.MM.yyyy', { locale: de })}</span>
              </div>
              <div className="pt-4 border-t border-[hsl(var(--border))]">
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full"
                  onClick={() => setDeleteDialog({ open: true, customer: customerDetail })}
                  data-testid="delete-customer-detail"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Kunde löschen
                </Button>
              </div>
              <div className="pt-4 border-t border-[hsl(var(--border))]">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Buchungen</p>
                    <p className="text-xl font-semibold">{customerDetail.bookings?.length || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Gesamtumsatz</p>
                    <p className="text-xl font-semibold">CHF {formatPrice(customerDetail.total_spent || 0)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Booking History */}
          <div className="lg:col-span-2">
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle>Buchungshistorie</CardTitle>
              </CardHeader>
              <CardContent>
                {customerDetail.bookings?.length > 0 ? (
                  <div className="space-y-3">
                    {customerDetail.bookings.map((booking) => (
                      <motion.div
                        key={booking.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="p-4 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted)/0.3)] transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <MapPin className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                              <span className="font-medium">{booking.location?.name || '-'}</span>
                            </div>
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">
                              {booking.start_date?.split('T')[0]} - {booking.end_date?.split('T')[0]}
                            </p>
                          </div>
                          <div className="text-right">
                            {getStatusBadge(booking.status)}
                            <p className="text-sm font-semibold mt-1">CHF {formatPrice(booking.total_price)}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-[hsl(var(--muted-foreground))]">Keine Buchungen vorhanden</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Customer List View
  return (
    <div data-testid="admin-customers-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold">
            {viewMode === 'customers' ? 'Kundenverwaltung' : 'Benutzerverwaltung'}
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            {viewMode === 'customers' ? `${customers.length} Kunden` : `${allUsers.length} Benutzer`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'customers' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('customers')}
            data-testid="view-customers-tab"
          >
            <Users className="w-4 h-4 mr-1" />
            Kunden
          </Button>
          <Button
            variant={viewMode === 'users' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('users')}
            data-testid="view-users-tab"
          >
            <Shield className="w-4 h-4 mr-1" />
            Alle Benutzer
          </Button>
        </div>
      </div>

      {viewMode === 'customers' ? (
        <>
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <Input
                placeholder="Suchen nach Name oder E-Mail..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
                data-testid="customers-search-input"
              />
            </div>
          </div>

          {customers.length === 0 ? (
            <Card className="card-shadow">
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))] opacity-50" />
                <h3 className="text-lg font-semibold mb-2">Keine Kunden</h3>
                <p className="text-[hsl(var(--muted-foreground))]">
                  {searchTerm ? 'Keine Kunden gefunden.' : 'Es gibt noch keine registrierten Kunden.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-shadow">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]">
                        <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Kunde</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Buchungen</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Bestätigt</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Umsatz</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Registriert</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map((customer, index) => (
                        <motion.tr
                          key={customer.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: index * 0.02 }}
                          className="border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--muted)/0.5)] transition-colors cursor-pointer"
                          onClick={() => handleSelectCustomer(customer)}
                          data-testid={`customer-row-${customer.id}`}
                        >
                          <td className="py-4 px-4">
                            <p className="font-medium">{customer.name}</p>
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">{customer.email}</p>
                          </td>
                          <td className="py-4 px-4">
                            <span className="font-medium">{customer.booking_count || 0}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="font-medium text-emerald-600">{customer.confirmed_count || 0}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="font-semibold">CHF {formatPrice(customer.total_spent || 0)}</span>
                          </td>
                          <td className="py-4 px-4 text-sm text-[hsl(var(--muted-foreground))]">
                            {format(new Date(customer.created_at), 'dd.MM.yyyy', { locale: de })}
                          </td>
                          <td className="py-4 px-4">
                            <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        /* Users/Role Management View */
        <div className="space-y-6">
        <Card className="card-shadow">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Benutzer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Rolle</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Registriert</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((u, index) => (
                    <motion.tr
                      key={u.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                      className="border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--muted)/0.5)] transition-colors"
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="py-4 px-4">
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{u.email}</p>
                      </td>
                      <td className="py-4 px-4">
                        {u.role === 'admin' ? (
                          <Badge className="bg-violet-50 text-violet-700 border-violet-200">
                            <Shield className="w-3 h-3 mr-1" />
                            Admin
                          </Badge>
                        ) : u.role === 'employee' ? (
                          <Badge className="bg-blue-50 text-blue-700 border-blue-200">
                            <Shield className="w-3 h-3 mr-1" />
                            Mitarbeiter
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                            Kunde
                          </Badge>
                        )}
                      </td>
                      <td className="py-4 px-4 text-sm text-[hsl(var(--muted-foreground))]">
                        {format(new Date(u.created_at), 'dd.MM.yyyy', { locale: de })}
                      </td>
                      <td className="py-4 px-4">
                        {u.id === currentAdmin?.id ? (
                          <span className="text-xs text-[hsl(var(--muted-foreground))]">Sie selbst</span>
                        ) : u.role === 'customer' ? (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setRoleDialog({ open: true, user: u, newRole: 'employee' })}
                              data-testid={`promote-employee-${u.id}`}
                            >
                              <Shield className="w-3.5 h-3.5 mr-1" />
                              Mitarbeiter
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setRoleDialog({ open: true, user: u, newRole: 'admin' })}
                              data-testid={`promote-user-${u.id}`}
                            >
                              <Shield className="w-3.5 h-3.5 mr-1" />
                              Admin
                            </Button>
                          </div>
                        ) : u.role === 'employee' ? (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setRoleDialog({ open: true, user: u, newRole: 'admin' })}
                              data-testid={`promote-user-${u.id}`}
                            >
                              <Shield className="w-3.5 h-3.5 mr-1" />
                              Admin
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-amber-600 border-amber-200 hover:bg-amber-50"
                              onClick={() => setRoleDialog({ open: true, user: u, newRole: 'customer' })}
                              data-testid={`demote-user-${u.id}`}
                            >
                              <ShieldOff className="w-3.5 h-3.5 mr-1" />
                              Kunde
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-amber-600 border-amber-200 hover:bg-amber-50"
                            onClick={() => setRoleDialog({ open: true, user: u, newRole: 'customer' })}
                            data-testid={`demote-user-${u.id}`}
                          >
                            <ShieldOff className="w-3.5 h-3.5 mr-1" />
                            Zum Kunden
                          </Button>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        <Card className="card-shadow" data-testid="role-history-card">
          <CardHeader>
            <CardTitle>Letzte Rollenänderungen</CardTitle>
            <CardDescription>Zur Nachverfolgung von Admin- und Mitarbeiterfreigaben.</CardDescription>
          </CardHeader>
          <CardContent>
            {roleHistory.length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Noch keine Rollenänderungen vorhanden.</p>
            ) : (
              <div className="space-y-3">
                {roleHistory.map((entry) => (
                  <div key={entry.id} className="rounded-lg border border-[hsl(var(--border))] px-4 py-3" data-testid={`role-history-${entry.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-sm">{entry.details?.target_user || 'Benutzer'}: {entry.details?.old_role || '-'} → {entry.details?.new_role || '-'}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">durch {entry.admin_name || 'Admin'}</p>
                      </div>
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">{entry.created_at ? format(new Date(entry.created_at), 'dd.MM.yyyy HH:mm', { locale: de }) : '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* Role Change Dialog */}
      <Dialog open={roleDialog.open} onOpenChange={(open) => !open && setRoleDialog({ open: false, user: null, newRole: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rolle ändern</DialogTitle>
            <DialogDescription>
              Möchten Sie <strong>{roleDialog.user?.name}</strong> ({roleDialog.user?.email}) wirklich 
              {roleDialog.newRole === 'admin' ? ' zum Admin befördern' : 
               roleDialog.newRole === 'employee' ? ' zum Mitarbeiter befördern' : ' zum Kunden zurückstufen'}?
              {roleDialog.newRole === 'admin' && ' Admins haben vollen Zugriff auf alle Verwaltungsfunktionen.'}
              {roleDialog.newRole === 'employee' && ' Mitarbeiter-Berechtigungen werden in den Einstellungen konfiguriert.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog({ open: false, user: null, newRole: '' })}>
              Abbrechen
            </Button>
            <Button
              onClick={changeUserRole}
              disabled={roleChanging}
              data-testid="confirm-role-change-button"
            >
              {roleChanging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (roleDialog.newRole === 'customer' ? <ShieldOff className="w-4 h-4 mr-2" /> : <Shield className="w-4 h-4 mr-2" />)}
              {roleDialog.newRole === 'admin' ? 'Zum Admin befördern' : 
               roleDialog.newRole === 'employee' ? 'Zum Mitarbeiter befördern' : 'Zum Kunden zurückstufen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Customer Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, customer: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Kunde löschen</DialogTitle>
            <DialogDescription>
              Möchten Sie <strong>{deleteDialog.customer?.name}</strong> ({deleteDialog.customer?.email}) wirklich löschen?
              Der Account wird entfernt, bestehende Buchungen bleiben erhalten.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, customer: null })}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={deleteCustomer}
              disabled={deleteLoading}
              data-testid="confirm-delete-customer"
            >
              {deleteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomers;
